import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { SettingsHeader } from "../index";
import {
  Button,
  Input,
  Modal,
  Select,
  SettingsPanel,
  TextArea,
} from "../../../shared";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/access-control";
import type { NotificationPriority, NotificationType } from "@/types/api";
import {
  DEFAULT_NOTIFICATION_TEMPLATES,
  type NotificationTemplate,
} from "@/constants/notificationTemplates";
import { useCreateNotificationMutation } from "@/redux/services/notifications/notificationApi";
import { useGetUsersQuery } from "@/redux/services/users/usersApi";
import { USER } from "@/config/defaultSettings";

type UserOption = { label: string; value: string };

const NOTIFICATION_TYPES: Array<{ label: string; value: NotificationType }> = [
  { label: "Dispatch", value: "DISPATCH" },
  { label: "Service request", value: "SERVICE_REQUEST" },
  { label: "Maintenance", value: "MAINTENANCE" },
  { label: "Invoice", value: "INVOICE" },
  { label: "System", value: "SYSTEM" },
  { label: "Emergency", value: "EMERGENCY" },
];

const PRIORITIES: Array<{ label: string; value: NotificationPriority }> = [
  { label: "Low", value: "low" },
  { label: "Medium", value: "medium" },
  { label: "High", value: "high" },
  { label: "Urgent", value: "urgent" },
];

function getCurrentUserId(): string | null {
  try {
    const raw = localStorage.getItem(USER);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?._id || parsed?.id || null;
  } catch {
    return null;
  }
}

function getStorageKey(parentCompanyId?: string | null) {
  // keep per-tenant templates isolated when possible
  return `attune:notificationTemplates:v1:${parentCompanyId || "global"}`;
}

function safeParseTemplates(value: string | null): NotificationTemplate[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function normalizeRecipientIds(input: string): string[] {
  return String(input || "")
    .split(/[,\n]/g)
    .map((s) => s.trim())
    .filter(Boolean);
}

const NotificationTemplatesPage = () => {
  const { hasPermission } = usePermissions();
  const canAccess = hasPermission(PERMISSIONS.SETTINGS_TEMPLATES_MANAGE);

  const [search, setSearch] = useState("");
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);

  const [editingTemplate, setEditingTemplate] =
    useState<NotificationTemplate | null>(null);

  const [customTemplates, setCustomTemplates] = useState<NotificationTemplate[]>(
    []
  );

  const [sendForm, setSendForm] = useState<{
    recipientIds: string;
    recipientsPicked: UserOption[];
    type: NotificationType;
    priority: NotificationPriority;
    title: string;
    message: string;
    metadataJson: string;
  }>({
    recipientIds: "",
    recipientsPicked: [],
    type: "SYSTEM",
    priority: "low",
    title: "",
    message: "",
    metadataJson: "{}",
  });

  const [templateForm, setTemplateForm] = useState<{
    name: string;
    description: string;
    type: NotificationType;
    priority: NotificationPriority;
    title: string;
    message: string;
    metadataJson: string;
  }>({
    name: "",
    description: "",
    type: "SYSTEM",
    priority: "low",
    title: "",
    message: "",
    metadataJson: "{}",
  });

  const currentUserId = useMemo(() => getCurrentUserId(), []);

  // Load parentCompany from stored user (best-effort) to scope custom templates
  const parentCompanyId = useMemo(() => {
    try {
      const raw = localStorage.getItem(USER);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.parentCompany || parsed?.organization || null;
    } catch {
      return null;
    }
  }, []);

  const storageKey = useMemo(() => getStorageKey(parentCompanyId), [
    parentCompanyId,
  ]);

  useEffect(() => {
    const stored = safeParseTemplates(localStorage.getItem(storageKey));
    setCustomTemplates(stored);
  }, [storageKey]);

  const templates = useMemo(() => {
    const custom = [...customTemplates].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "")
    );
    return [...DEFAULT_NOTIFICATION_TEMPLATES, ...custom];
  }, [customTemplates]);

  const filteredTemplates = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) => {
      const haystack = `${t.name} ${t.description || ""} ${t.title} ${
        t.message
      } ${t.type} ${t.priority}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [templates, search]);

  const { data: usersData, isLoading: usersLoading } = useGetUsersQuery(
    { page: 1, limit: 50, search: search || undefined },
    { skip: !canAccess }
  );

  const userOptions: UserOption[] = useMemo(() => {
    const users = usersData?.users || [];
    return users
      .filter((u: any) => u?._id)
      .map((u: any) => ({
        value: String(u._id),
        label: `${u.fullName || `${u.firstName || ""} ${u.lastName || ""}`.trim()}${
          u.email ? ` (${u.email})` : ""
        }`.trim(),
      }))
      .sort((a: UserOption, b: UserOption) => a.label.localeCompare(b.label));
  }, [usersData]);

  const [createNotification, { isLoading: isSending }] =
    useCreateNotificationMutation();

  function openNewTemplateModal() {
    setEditingTemplate(null);
    setTemplateForm({
      name: "",
      description: "",
      type: "SYSTEM",
      priority: "low",
      title: "",
      message: "",
      metadataJson: "{}",
    });
    setTemplateModalOpen(true);
  }

  function openEditTemplateModal(template: NotificationTemplate) {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name || "",
      description: template.description || "",
      type: template.type,
      priority: template.priority,
      title: template.title,
      message: template.message,
      metadataJson: JSON.stringify(template.metadata || {}, null, 2),
    });
    setTemplateModalOpen(true);
  }

  function upsertCustomTemplate(next: NotificationTemplate) {
    setCustomTemplates((prev) => {
      const exists = prev.some((t) => t.id === next.id);
      const updated = exists
        ? prev.map((t) => (t.id === next.id ? next : t))
        : [next, ...prev];
      localStorage.setItem(storageKey, JSON.stringify(updated));
      return updated;
    });
  }

  function deleteCustomTemplate(id: string) {
    setCustomTemplates((prev) => {
      const updated = prev.filter((t) => t.id !== id);
      localStorage.setItem(storageKey, JSON.stringify(updated));
      return updated;
    });
  }

  function openSendModalFromTemplate(template: NotificationTemplate) {
    setSendForm({
      recipientIds: "",
      recipientsPicked: [],
      type: template.type,
      priority: template.priority,
      title: template.title,
      message: template.message,
      metadataJson: JSON.stringify(template.metadata || {}, null, 2),
    });
    setSendModalOpen(true);
  }

  function openSendCustomModal() {
    setSendForm({
      recipientIds: "",
      recipientsPicked: [],
      type: "SYSTEM",
      priority: "low",
      title: "",
      message: "",
      metadataJson: "{}",
    });
    setSendModalOpen(true);
  }

  async function handleSaveTemplate() {
    const name = templateForm.name.trim();
    if (!name) {
      toast.error("Template name is required");
      return;
    }
    if (!templateForm.title.trim() || !templateForm.message.trim()) {
      toast.error("Title and message are required");
      return;
    }

    let metadata: Record<string, any> = {};
    try {
      metadata = templateForm.metadataJson
        ? JSON.parse(templateForm.metadataJson)
        : {};
    } catch {
      toast.error("Metadata must be valid JSON");
      return;
    }

    const id =
      editingTemplate?.id || `custom-${Date.now()}-${Math.random().toString(16).slice(2)}`;

    upsertCustomTemplate({
      id,
      name,
      description: templateForm.description?.trim() || "",
      type: templateForm.type,
      priority: templateForm.priority,
      title: templateForm.title.trim(),
      message: templateForm.message.trim(),
      metadata,
      isSystem: false,
    });

    toast.success("Notification template saved");
    setTemplateModalOpen(false);
  }

  async function handleSend() {
    const recipientIds = Array.from(
      new Set([
        ...normalizeRecipientIds(sendForm.recipientIds),
        ...sendForm.recipientsPicked.map((r) => r.value),
      ])
    );

    if (recipientIds.length === 0) {
      toast.error("Please add at least one recipient user ID");
      return;
    }
    if (!sendForm.title.trim() || !sendForm.message.trim()) {
      toast.error("Title and message are required");
      return;
    }

    let metadata: Record<string, any> = {};
    try {
      metadata = sendForm.metadataJson ? JSON.parse(sendForm.metadataJson) : {};
    } catch {
      toast.error("Metadata must be valid JSON");
      return;
    }

    try {
      // API only supports single-recipient create; we fan out for multiple recipients.
      await Promise.all(
        recipientIds.map((recipient) =>
          createNotification({
            recipient,
            type: sendForm.type,
            title: sendForm.title.trim(),
            message: sendForm.message.trim(),
            priority: sendForm.priority,
            metadata,
          }).unwrap()
        )
      );
      toast.success(`Notification sent to ${recipientIds.length} user(s)`);
      setSendModalOpen(false);
    } catch (err: any) {
      toast.error(err?.data?.message || err?.message || "Failed to send");
    }
  }

  if (!canAccess) {
    return (
      <div className="space-y-4">
        <SettingsHeader title="Notification Templates" />
        <SettingsPanel
          title="Access restricted"
          subtitle="Only admins can access this tool."
          data={[
            {
              content: (
                <div className="text-sm text-gray-600">
                  You don’t have permission to view this page.
                </div>
              ),
            },
          ]}
        />
      </div>
    );
  }

  const actions = (
    <div className="flex items-center space-x-2">
      <Button type="alternative" onClick={openSendCustomModal}>
        Send Custom
      </Button>
      <Button type="primary" onClick={openNewTemplateModal}>
        New Template
      </Button>
    </div>
  );

  return (
    <div className="space-y-4">
      <SettingsHeader title="Notification Templates" actions={actions} />

      <SettingsPanel
        title="Templates"
        subtitle="Pick a template, then send it to one or more users. Custom templates are stored in your browser for this tenant."
        data={[
          {
            title: "Search",
            subtitle: "Filter templates and users",
            content: (
              <Input
                value={search}
                onChange={(t) => setSearch(typeof t === "string" ? t : t.value)}
                placeholder="Search templates/users…"
                clearable
              />
            ),
          },
          {
            content: (
              <div className="bg-white p-4 rounded-b-xl">
                {filteredTemplates.length === 0 ? (
                  <div className="text-sm text-gray-600">No templates found.</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {filteredTemplates.map((t) => {
                      const isCustom = !t.isSystem;
                      return (
                        <div
                          key={t.id}
                          className="border rounded-lg p-4 bg-white space-y-2"
                        >
                          <div className="flex items-start justify-between">
                            <div className="min-w-0">
                              <div className="font-semibold text-gray-900 truncate">
                                {t.name}
                              </div>
                              {t.description ? (
                                <div className="text-xs text-gray-500 mt-0.5">
                                  {t.description}
                                </div>
                              ) : null}
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="text-[10px] px-2 py-1 rounded bg-gray-100 text-gray-700">
                                {t.type}
                              </span>
                              <span className="text-[10px] px-2 py-1 rounded bg-gray-100 text-gray-700">
                                {t.priority}
                              </span>
                            </div>
                          </div>

                          <div className="text-sm text-gray-800">
                            <div className="font-medium">{t.title}</div>
                            <div className="text-xs text-gray-600 mt-0.5 line-clamp-3">
                              {t.message}
                            </div>
                          </div>

                          <div className="flex items-center space-x-2 pt-2">
                            <Button
                              size="xs"
                              type="primary"
                              onClick={() => openSendModalFromTemplate(t)}
                            >
                              Send
                            </Button>
                            {isCustom && (
                              <>
                                <Button
                                  size="xs"
                                  type="alternative"
                                  onClick={() => openEditTemplateModal(t)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="xs"
                                  type="danger"
                                  onClick={() => deleteCustomTemplate(t.id)}
                                >
                                  Delete
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ),
          },
        ]}
      />

      {/* Template create/edit modal */}
      <Modal
        visible={templateModalOpen}
        setVisible={setTemplateModalOpen}
        title={editingTemplate ? "Edit notification template" : "New notification template"}
        submitText="Save"
        cancelText="Cancel"
        confirm={handleSaveTemplate}
        disabled={false}
      >
        <div className="space-y-3">
          <Input
            label="Template name"
            value={templateForm.name}
            onChange={(t) =>
              setTemplateForm((p) => ({
                ...p,
                name: typeof t === "string" ? t : t.value,
              }))
            }
            placeholder="e.g. Winter weather advisory"
          />

          <Input
            label="Description (optional)"
            value={templateForm.description}
            onChange={(t) =>
              setTemplateForm((p) => ({
                ...p,
                description: typeof t === "string" ? t : t.value,
              }))
            }
            placeholder="Short note about when to use this template"
          />

          <div className="grid grid-cols-2 gap-2">
            <Select
              label="Type"
              value={templateForm.type}
              options={NOTIFICATION_TYPES}
              onChange={(opt) =>
                setTemplateForm((p) => ({ ...p, type: opt.value }))
              }
            />
            <Select
              label="Priority"
              value={templateForm.priority}
              options={PRIORITIES}
              onChange={(opt) =>
                setTemplateForm((p) => ({ ...p, priority: opt.value }))
              }
            />
          </div>

          <Input
            label="Title"
            value={templateForm.title}
            onChange={(t) =>
              setTemplateForm((p) => ({
                ...p,
                title: typeof t === "string" ? t : t.value,
              }))
            }
            placeholder="Short title shown in notification"
          />

          <TextArea
            label="Message"
            value={templateForm.message}
            onChange={(e) =>
              setTemplateForm((p) => ({ ...p, message: e.target.value }))
            }
            rows={5}
            placeholder="Write the notification message…"
          />

          <TextArea
            label="Metadata (JSON, optional)"
            value={templateForm.metadataJson}
            onChange={(e) =>
              setTemplateForm((p) => ({ ...p, metadataJson: e.target.value }))
            }
            rows={5}
            placeholder='{"source":"admin-tool"}'
          />
        </div>
      </Modal>

      {/* Send modal */}
      <Modal
        visible={sendModalOpen}
        setVisible={setSendModalOpen}
        title="Send notification"
        submitText={isSending ? "Sending…" : "Send"}
        cancelText="Cancel"
        confirm={handleSend}
        loading={isSending}
        disabled={isSending}
      >
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <Select
              label="Type"
              value={sendForm.type}
              options={NOTIFICATION_TYPES}
              onChange={(opt) =>
                setSendForm((p) => ({ ...p, type: opt.value }))
              }
            />
            <Select
              label="Priority"
              value={sendForm.priority}
              options={PRIORITIES}
              onChange={(opt) =>
                setSendForm((p) => ({ ...p, priority: opt.value }))
              }
            />
          </div>

          <Input
            label="Title"
            value={sendForm.title}
            onChange={(t) =>
              setSendForm((p) => ({
                ...p,
                title: typeof t === "string" ? t : t.value,
              }))
            }
            placeholder="Short title shown in notification"
          />

          <TextArea
            label="Message"
            value={sendForm.message}
            onChange={(e) =>
              setSendForm((p) => ({ ...p, message: e.target.value }))
            }
            rows={5}
            placeholder="Write the notification message…"
          />

          <div className="grid grid-cols-1 gap-2">
            <TextArea
              label="Recipients (user IDs, comma or newline separated)"
              value={sendForm.recipientIds}
              onChange={(e) =>
                setSendForm((p) => ({ ...p, recipientIds: e.target.value }))
              }
              rows={3}
              placeholder="64f... , 64a...\n..."
            />

            <div className="text-xs text-gray-500">
              Optional: pick users from the list below (first 50). This fills in
              user IDs automatically.
            </div>
            <Select
              label="Pick users"
              value={sendForm.recipientsPicked?.length ? "Selected" : ""}
              options={userOptions.map((u) => ({
                label: u.label,
                value: u.value,
              }))}
              onChange={(opt) => {
                const picked = { label: opt.label, value: opt.value };
                setSendForm((p) => {
                  const exists = p.recipientsPicked.some(
                    (r) => r.value === picked.value
                  );
                  const next = exists
                    ? p.recipientsPicked
                    : [picked, ...p.recipientsPicked];
                  return { ...p, recipientsPicked: next };
                });
              }}
              disabled={usersLoading}
            />

            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-600">
                Picked: {sendForm.recipientsPicked.length}
              </div>
              <div className="flex items-center space-x-2">
                {currentUserId && (
                  <Button
                    size="xs"
                    type="alternative"
                    onClick={() =>
                      setSendForm((p) => ({
                        ...p,
                        recipientIds: p.recipientIds
                          ? `${p.recipientIds},${currentUserId}`
                          : currentUserId,
                      }))
                    }
                  >
                    Add me
                  </Button>
                )}
                <Button
                  size="xs"
                  type="alternative"
                  onClick={() => setSendForm((p) => ({ ...p, recipientsPicked: [] }))}
                >
                  Clear picked
                </Button>
              </div>
            </div>
          </div>

          <TextArea
            label="Metadata (JSON, optional)"
            value={sendForm.metadataJson}
            onChange={(e) =>
              setSendForm((p) => ({ ...p, metadataJson: e.target.value }))
            }
            rows={5}
            placeholder='{"source":"settings-notification-tool"}'
          />
        </div>
      </Modal>
    </div>
  );
};

export default NotificationTemplatesPage;




